import React from 'react';
import { SafeAreaView, View, StyleSheet, Text, ScrollView, TextInput, TouchableOpacity, Modal as RNModal, Image, Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { ModuleHeader, Card, BottomTabBar, type BottomTabItem, AvatarCard, Avatar, VipCard, ProfileActions, ProfileModal, listPresetAvatarKeys, getPresetAvatarAsset, PlatformIcon as Icon } from '@cubemeet/ui';
import * as authActions from '@cubemeet/core/store/slices/authSlice';
import { updateProfile } from '@cubemeet/core/store/slices/authSlice';
import { AVATAR_UPLOAD_ENDPOINT } from '@cubemeet/core/config/cloudflare';
import { getCurrentUser } from '@cubemeet/core/store/slices/authSlice';
import { useGlobalToast } from '../../contexts/ToastContext';

const ProfileModule: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const auth = useSelector((s: RootState) => (s as any)?.auth ?? {}) as any;
  const profile = auth?.profile;

  // use Redux profile directly; do not use localStorage fallback
  const profileToShow = profile;

  // inline edit state
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({ display_name: '', phone: '', bio: '' });
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  // 社交账号（最多 5 个）
  type SocialEntry = { platform: string; id?: string; url?: string };
  const SUPPORTED_PLATFORMS: Array<{ key: string; label: string }> = [
    { key: 'youtube', label: 'YouTube' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'xiaohongshu', label: '小红书' },
    { key: 'tiktok', label: 'TikTok/抖音' },
    { key: 'x', label: 'X' },
  ];
  const [socials, setSocials] = React.useState<SocialEntry[]>([]);

  // sync form when profile loads/changes
  React.useEffect(() => {
    if (profileToShow) {
      setForm({
        display_name: profileToShow.display_name ?? '',
        phone: profileToShow.phone ?? '',
        bio: profileToShow.bio ?? '',
      });
      // 初始化社交账号编辑状态
      try {
        const raw = (profileToShow as any)?.social_accounts || null;
        if (raw && typeof raw === 'object') {
          const arr: SocialEntry[] = [];
          for (const p of SUPPORTED_PLATFORMS) {
            const v = (raw as any)[p.key];
            if (v && typeof v === 'object') {
              arr.push({ platform: p.key, id: (v as any).id || (v as any).handle || '', url: (v as any).url || '' });
            }
          }
          setSocials(arr);
        } else {
          setSocials([]);
        }
      } catch {
        setSocials([]);
      }
    }
  }, [profileToShow]);

  
  // Ensure we have the latest user/profile when the profile page is opened.
  // Guard to avoid dispatching repeatedly while a global auth check is in progress
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // If auth already has a user or is currently loading, skip a redundant refresh.
        if (!mounted) return;
        if (auth?.user) return;
        if (auth?.loading) return;

        // Refresh current user/profile from server (no-op if already fresh)
        await dispatch(getCurrentUser()).unwrap();
      } catch (e) {
        // ignore errors here; UI will show empty state
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, auth?.user, auth?.loading]);

  const [signOutModalVisible, setSignOutModalVisible] = React.useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = React.useState(false);
  const { showSuccess, showError } = useGlobalToast();

  const handleLogout = async () => {
    setSignOutModalVisible(true);
  };

  const handleCancel = () => {
    setEditing(false);
    if (profileToShow) setForm({ display_name: profileToShow.display_name ?? '', phone: profileToShow.phone ?? '', bio: profileToShow.bio ?? '' });
    setErrorMsg(null);
  };

  const handleSave = async () => {
    // save
    if (!profileToShow) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const payload: any = {};
      if (form.display_name !== profileToShow.display_name) payload.display_name = form.display_name;
      if (form.phone !== profileToShow.phone) payload.phone = form.phone;
      if (form.bio !== profileToShow.bio) payload.bio = form.bio;
      // 组装 social_accounts 对象（仅保存非空，最多 5 个）
      const socialObject = socials
        .filter((e) => (e.id && e.id.trim()) || (e.url && e.url.trim()))
        .slice(0, 5)
        .reduce((acc: Record<string, any>, cur) => {
          acc[cur.platform] = {
            id: cur.id?.trim() || null,
            url: cur.url?.trim() || null,
          };
          return acc;
        }, {} as Record<string, any>);
      const prevSocials = (profileToShow as any)?.social_accounts || {};
      if (JSON.stringify(prevSocials) !== JSON.stringify(socialObject)) {
        payload.social_accounts = socialObject;
      }
      if (Object.keys(payload).length === 0) {
        setEditing(false);
        setSaving(false);
        return;
      }
      await dispatch(updateProfile({ userId: profileToShow.id, profileData: payload })).unwrap();
      setEditing(false);
    } catch (e: any) {
      console.error('保存 profile 失敗', e);
      setErrorMsg(e?.message || '保存失敗');
    } finally {
      setSaving(false);
    }
  };

  const confirmSignOut = async () => {
    try {
      if ((authActions as any)?.signOut) {
        await dispatch((authActions as any).signOut());
      }
    } finally {
      setSignOutModalVisible(false);
      if (typeof window !== 'undefined') window.location.replace('/');
    }
  };

  // --- Social accounts helpers ---
  const addPlatform = (key: string) => {
    setSocials((prev) => {
      if (prev.find((e) => e.platform === key)) return prev;
      if (prev.length >= 5) return prev;
      return [...prev, { platform: key, id: '', url: '' }];
    });
  };
  const removePlatform = (key: string) => setSocials((prev) => prev.filter((e) => e.platform !== key));
  const updatePlatformValue = (key: string, value: string) => {
    setSocials((prev) => prev.map((e) => (e.platform === key ? { ...e, id: value, url: value } : e)));
  };

  const items: BottomTabItem[] = [
    { key: 'settings', label: 'Settings', icon: 'settings' },
    { key: 'notifications', label: 'Notifications', icon: 'notifications', badge: 2 },
    { key: 'hub', label: '', icon: 'apps' },
    { key: 'profile', label: 'Profile', icon: 'person' },
    { key: 'friends', label: 'Friends', icon: 'people' },
  ];

  const onTabPress = (i: number, key: string) => {
    const map: Record<string, string> = {
      settings: 'dashboard',
      notifications: 'notifications',
      hub: 'dashboard',
      profile: 'profile',
      friends: 'friends',
    };
    const target = map[key] || 'dashboard';
    if (typeof window !== 'undefined') window.location.hash = target;
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.mainContainer}>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          <View style={styles.topBanner} />
          <View style={styles.contentBody}>
            {/* Profile header card - centered avatar + basic info */}
            <Card style={styles.headerCard}>
          {/* edit (pencil) button or save/cancel actions at card top-right */}
          {editing ? (
            <View style={styles.editActions}>
              <TouchableOpacity onPress={handleSave} style={styles.editActionPrimary} accessibilityRole="button" disabled={saving}>
                <Icon name="done" family="MaterialIcons" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity onPress={handleCancel} style={styles.editAction} accessibilityRole="button">
                <Icon name="close" family="MaterialIcons" size={20} color="#6B7280" />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity onPress={() => setEditing(true)} style={styles.editPencil} accessibilityRole="button">
              <Icon name="edit" size={20} color="#2563EB" />
            </TouchableOpacity>
          )}
          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            {/* Avatar + unified icons: centered avatar, edit button and camera upload */}
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              <View style={{ position: 'relative' }}>
                <Avatar avatarUrl={profileToShow?.avatar_url ?? null} size="xxl" displayName={profileToShow?.display_name ?? undefined} email={profileToShow?.phone ?? undefined} showEditIcon={false} />

                {/* VIP / Member badge will be shown on its own line below */}

                {/* gallery (preset) picker - bottom-left */}
                <TouchableOpacity onPress={() => setShowAvatarPicker(true)} style={styles.galleryOverlaySmall} accessibilityRole="button">
                  <Icon name="photo_library" family="MaterialIcons" size={16} color="#2563EB" />
                </TouchableOpacity>

                {/* camera (upload) overlay - bottom-right */}
                <TouchableOpacity onPress={() => {
                  const el = document.getElementById('cm-avatar-file-input') as HTMLInputElement | null;
                  if (el) el.click();
                }} style={styles.cameraOverlaySmall} accessibilityRole="button">
                  <Icon name="photo_camera" family="MaterialIcons" size={16} color="#fff" />
                </TouchableOpacity>
              </View>


              <Text style={styles.headerName}>{profileToShow?.display_name ?? profileToShow?.username ?? '我的'}</Text>
              <Text style={styles.headerSubtitle}>{profileToShow?.phone ?? profileToShow?.email ?? ''}</Text>

              {/* bio should appear under nickname */}
              {profileToShow?.bio ? (
                <Text style={styles.bio}>{profileToShow.bio}</Text>
              ) : null}

              {/* badge line (make pill wider) */}
              <View style={{ marginTop: 12 }}>
                <View style={profileToShow?.is_vip_active ? styles.vipPill : styles.memberPill}>
                  <Text style={styles.vipPillText}>{profileToShow?.is_vip_active ? 'VIP' : 'Member'}</Text>
                </View>
              </View>

            </View>
          </View>

            {/* hidden file input for avatar upload (web only) */}
            <input id="cm-avatar-file-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e: any) => {
            const file = e?.target?.files?.[0];
            if (!file) return;
            if (!profileToShow) return;
            // basic client-side size/type checks (mirror worker)
            const allowed = ['image/jpeg','image/jpg','image/png','image/webp'];
            if (!allowed.includes(file.type)) { showError('只接受 JPEG/PNG/WebP 檔案'); return; }
            const maxSize = 3 * 1024 * 1024;
            if (file.size > maxSize) { showError('檔案過大，最大 3MB'); return; }

            // upload to worker
            try {
              setSaving(true);
              const form = new FormData();
              form.append('file', file);
              form.append('userId', profileToShow.id);

              const resp = await fetch(AVATAR_UPLOAD_ENDPOINT, { method: 'POST', body: form });
              const json = await resp.json();
              if (!resp.ok || !json?.success) {
                console.error('avatar upload failed', json);
                showError('上傳失敗');
                return;
              }

              const url = json.data?.url;
              if (url) {
                // update profile avatar_url to CDN URL
                await dispatch(updateProfile({ userId: profileToShow.id, profileData: { avatar_url: url } })).unwrap();
                showSuccess('頭像上傳成功');
              }
            } catch (err) {
              console.error('avatar upload error', err);
              showError('上傳錯誤');
            } finally {
              setSaving(false);
              // clear input value so same file can be selected again if needed
              try { (document.getElementById('cm-avatar-file-input') as HTMLInputElement).value = ''; } catch(e){}
            }
          }} />

          {/* Avatar picker modal (preset avatars + camera) */}
          <RNModal visible={showAvatarPicker} transparent animationType="fade" onRequestClose={() => setShowAvatarPicker(false)}>
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              {/* backdrop */}
              <TouchableOpacity activeOpacity={1} onPress={() => setShowAvatarPicker(false)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)' }} />

              <View style={{ backgroundColor: '#fff', marginHorizontal: 20, borderRadius: 12, padding: 16, width: '90%', maxHeight: '80vh' }}>
                <Text style={{ fontSize: 16, fontWeight: '700', marginBottom: 12 }}>選擇預設頭像</Text>
                <ScrollView style={{ maxHeight: '60vh' }} contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap', paddingBottom: 12 }} showsVerticalScrollIndicator={false}>
                  {listPresetAvatarKeys().map((k) => {
                    const src = getPresetAvatarAsset(k as any) as any;
                    const imageSource = typeof src === 'string' ? { uri: src } : src;
                    return (
                      <TouchableOpacity key={k} onPress={async () => {
                        if (!profileToShow) return;
                        try {
                          setSaving(true);
                          await dispatch(updateProfile({ userId: profileToShow.id, profileData: { avatar_url: k } })).unwrap();
                          showSuccess('已選擇預設頭像');
                          setShowAvatarPicker(false);
                        } catch (err) {
                          console.error('選擇預設頭像失敗', err);
                          showError('更新頭像失敗');
                        } finally { setSaving(false); }
                      }} style={{ width: 72, height: 72, borderRadius: 36, overflow: 'hidden', marginRight: 12, marginBottom: 12, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' }}>
                        {src ? (
                          <Image source={imageSource as any} style={{ width: 72, height: 72, borderRadius: 36 }} />
                        ) : (
                          <View style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: '#E5E7EB' }} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>

                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12 }}>
                  <TouchableOpacity onPress={() => setShowAvatarPicker(false)} style={{ paddingHorizontal: 12, paddingVertical: 8, marginRight: 8 }}>
                    <Text>取消</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => {
                    const el = document.getElementById('cm-avatar-file-input') as HTMLInputElement | null;
                    if (el) el.click();
                  }} style={{ backgroundColor: '#2563EB', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 }}>
                    <Text style={{ color: '#fff' }}>拍照/上傳</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </RNModal>

          {/* inline edit controls */}
          {editing ? (
            <View style={{ marginTop: 12 }}>
              <Text style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>名稱</Text>
              <TextInput value={form.display_name} onChangeText={(v) => setForm((s) => ({ ...s, display_name: v }))} placeholder="輸入顯示名稱" style={{ padding: 8, borderRadius: 8, backgroundColor: '#fff', borderColor: '#E5E7EB', borderWidth: 1 }} />

              <Text style={{ fontSize: 13, color: '#374151', marginTop: 10, marginBottom: 6 }}>電話</Text>
              <TextInput value={form.phone} onChangeText={(v) => setForm((s) => ({ ...s, phone: v }))} placeholder="電話號碼" style={{ padding: 8, borderRadius: 8, backgroundColor: '#fff', borderColor: '#E5E7EB', borderWidth: 1 }} />

              <Text style={{ fontSize: 13, color: '#374151', marginTop: 10, marginBottom: 6 }}>簡介</Text>
              <TextInput value={form.bio} onChangeText={(v) => setForm((s) => ({ ...s, bio: v }))} placeholder="簡短介紹自己" multiline numberOfLines={3} style={{ padding: 8, borderRadius: 8, backgroundColor: '#fff', borderColor: '#E5E7EB', borderWidth: 1, minHeight: 80, textAlignVertical: 'top' }} />

              {/* Social accounts editor */}
              <Text style={{ fontSize: 13, color: '#374151', marginTop: 10, marginBottom: 6 }}>社交账号（最多 5 个）</Text>
              {socials.length > 0 ? (
                <View>
                  {socials.map((e) => (
                    <View key={e.platform} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                      <View style={{ minWidth: 96, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 16, backgroundColor: '#F3F4F6', marginRight: 8 }}>
                        <Text style={{ fontSize: 12, color: '#374151' }}>{SUPPORTED_PLATFORMS.find((p) => p.key === e.platform)?.label || e.platform}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <TextInput
                          value={e.url || e.id || ''}
                          onChangeText={(v) => updatePlatformValue(e.platform, v)}
                          placeholder="账号/链接"
                          style={{ padding: 8, borderRadius: 8, backgroundColor: '#fff', borderColor: '#E5E7EB', borderWidth: 1 }}
                        />
                      </View>
                      <TouchableOpacity onPress={() => removePlatform(e.platform)} style={{ marginLeft: 8, padding: 8 }} accessibilityRole="button">
                        <Icon name="delete" family="MaterialIcons" size={20} color="#9CA3AF" />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              ) : null}

              {/* Add platform chips */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }} contentContainerStyle={{ paddingVertical: 4 }}>
                {SUPPORTED_PLATFORMS.map((p) => {
                  const exists = !!socials.find((e) => e.platform === p.key);
                  const disabled = exists || socials.length >= 5;
                  return (
                    <TouchableOpacity
                      key={p.key}
                      onPress={() => addPlatform(p.key)}
                      disabled={disabled}
                      style={{
                        paddingVertical: 6,
                        paddingHorizontal: 10,
                        borderRadius: 16,
                        borderWidth: 1,
                        borderColor: disabled ? '#E5E7EB' : '#FB923C',
                        backgroundColor: disabled ? '#F9FAFB' : '#FFF7ED',
                        marginRight: 8,
                      }}
                    >
                      <Text style={{ color: disabled ? '#9CA3AF' : '#FB923C', fontSize: 12 }}>{exists ? `${p.label} 已添加` : `添加 ${p.label}`}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>


              {errorMsg ? <Text style={{ color: '#B91C1C', marginTop: 8 }}>{errorMsg}</Text> : null}
            </View>
          ) : null}
        </Card>

        

  {/* VIP card */}
        <View style={{ paddingHorizontal: 0 }}>
          <VipCard isVip={!!profileToShow?.is_vip_active} vipExpiresAt={profileToShow?.vip_expires_at} onUpgrade={() => { /* future hook */ }} />
        </View>

        {/* Quick actions */}
        <ProfileActions 
          onEditProfile={() => { if (typeof window !== 'undefined') window.location.hash = 'profile/edit'; }} 
          onOpenSettings={() => { if (typeof window !== 'undefined') window.location.hash = 'settings'; }} 
          onOpenFriends={() => navigate('/friends')}
        />

        {/* Sign out - large blue button to match mobile */}
        <View style={{ marginTop: 12 }}>
          <TouchableOpacity onPress={handleLogout} style={styles.signOutButton}>
            <Text style={{ color: '#fff', fontWeight: '700', textAlign: 'center' }}>退出登录</Text>
          </TouchableOpacity>
        </View>

            <ProfileModal visible={signOutModalVisible} title="退出登录" message="你确定要退出当前账号吗？" onCancel={() => setSignOutModalVisible(false)} onConfirm={confirmSignOut} />
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f6f7f8' },
  mainContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
    ...Platform.select({
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }
    }),
  },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  contentBody: { paddingHorizontal: 16, paddingBottom: 24 },
  topBanner: { height: 120, backgroundColor: '#3B82F6' },
  headerName: { fontSize: 20, fontWeight: '800', color: '#111827', marginTop: 8, textAlign: 'center' },
  headerSubtitle: { fontSize: 13, color: '#6B7280', marginTop: 4, textAlign: 'center' },
  vipBadge: { position: 'absolute', top: -6, left: 0, right: 0, alignSelf: 'center', backgroundColor: '#F59E0B', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  vipPill: { alignSelf: 'center', backgroundColor: '#F59E0B', width: 96, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center' },
  memberPill: { alignSelf: 'center', backgroundColor: '#E5E7EB', width: 96, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center' },
  vipPillText: { color: '#fff', fontWeight: '700', textAlign: 'center' },
  pointsPill: { marginTop: 10, backgroundColor: '#F7931E', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, alignSelf: 'center' },
  cameraOverlaySmall: { position: 'absolute', bottom: 0, right: 0, backgroundColor: '#2563EB', width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#fff' },
  galleryOverlaySmall: { position: 'absolute', bottom: 0, left: 0, backgroundColor: '#FFFFFF', width: 28, height: 28, borderRadius: 14, justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#FBCFE8' },
  signOutButton: { backgroundColor: '#2563EB', paddingVertical: 14, borderRadius: 12 },
  headerCard: { position: 'relative', paddingVertical: 28, marginBottom: 16, marginTop: -56, borderRadius: 12, paddingHorizontal: 16, backgroundColor: '#fff', zIndex: 10, ...Platform.select({ web: { boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }, default: { elevation: 6, shadowColor: '#000', shadowOpacity: 0.12, shadowRadius: 8, shadowOffset: { width: 0, height: 4 } as any } }) },
  bio: { marginTop: 10, color: '#374151', textAlign: 'center' },
  editPencil: { position: 'absolute', right: 16, top: 12, zIndex: 20, paddingHorizontal: 10, paddingVertical: 6 },
  editActions: { position: 'absolute', right: 12, top: 10, zIndex: 30, flexDirection: 'row', alignItems: 'center' },
  editActionPrimary: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#2563EB', justifyContent: 'center', alignItems: 'center', marginRight: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.12)' as any },
  editAction: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'transparent', justifyContent: 'center', alignItems: 'center' },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  headerText: { marginLeft: 12 },
  name: { fontSize: 18, fontWeight: '700', color: '#111827' },
  email: { fontSize: 13, color: '#6B7280', marginTop: 2 },
  card: { marginTop: 16 },
  cardRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontSize: 16, fontWeight: '600', color: '#111827' },
  actionBtn: { marginTop: 12, paddingVertical: 12, borderRadius: 12, backgroundColor: '#FEE2E2', alignItems: 'center' },
  logoutText: { color: '#B91C1C', fontWeight: '700' },
  bottomBarArea: { borderTopWidth: 1, borderTopColor: '#E5E7EB', paddingHorizontal: 24, paddingTop: 6, paddingBottom: 8, backgroundColor: '#FFFFFF', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
});

export default ProfileModule;
